/**
 * Escolher um entre poucos valores, com a lista desenhada por nós.
 *
 * ── Por que não o `<select>` ─────────────────────────────────────────────
 *
 * O `CampoSelecao` tirou o desenho do navegador do campo FECHADO, mas a lista
 * que abre continuava a do sistema operacional: no Mac, um menu cinza com
 * cantos e fonte que não são os nossos, serrilhado no tema escuro, e diferente
 * em cada navegador. Era a "fase 2" que a v3 do sistema visual deixou anotada
 * (`docs/design/sistema-visual.md`).
 *
 * ── O padrão: combobox só de escolha (WAI-ARIA APG) ──────────────────────
 *
 * O foco fica SEMPRE no botão; a opção ativa é anunciada por
 * `aria-activedescendant`. É o que o `<select>` faz por baixo, e é o que deixa
 * o leitor de tela ler "Prioridade, Urgente, caixa de combinação recolhida" sem
 * nenhum truque. O teclado é o do `<select>`:
 *
 * - fechado: ↓, ↑, Enter, Espaço ou Alt+↓ abrem; uma letra pula para a opção;
 * - aberto: ↓ ↑ andam (pulando as desabilitadas), Home e End vão às pontas,
 *   Enter ou Espaço escolhem, Esc fecha sem escolher, Tab fecha e segue.
 *
 * ── A lista na camada superior, fora do campo ────────────────────────────
 *
 * A lista vai para o `body` por portal, e não fica dentro do campo. O Safari
 * (WebKit) põe na camada superior um `popover` que mora dentro de um ancestral
 * com `transform` animado — a etapa do agendamento entra deslizando — com a
 * geometria errada: a lista aparecia no canto da tela, e só ia para o lugar ao
 * relayout seguinte; a busca às vezes a deixava em branco. No `body`, nenhum
 * ancestral do campo interfere.
 *
 * Pelo mesmo motivo do `MenuAcoes` (ver o cabeçalho de `menu-acoes.tsx`): a
 * coluna do painel rola, e lista absoluta dentro dela sairia recortada. Com o
 * atributo `popover` ela sobe de camada sem mudar de pai no DOM, e a conta de
 * posição é nossa: embaixo do campo, ao menos da largura dele, virando para
 * cima quando não cabe. A lista pode ser mais larga que o campo (um nome com o
 * motivo de não poder ser escolhido): aí ela se alinha pela direita do campo,
 * e nunca passa da borda da tela.
 *
 * ── Com busca (`buscavel`) ───────────────────────────────────────────────
 *
 * Para listas longas (as oportunidades do contato, os médicos da clínica), a
 * lista abre com um campo de busca no topo e o foco vai para ele: digitar
 * filtra pelo nome, sem acento nem caixa, e as setas, o Enter e o Esc valem ali
 * como no botão. Escolhido ou fechado com Esc, o foco volta para o campo.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icone } from '@/shared/ui/icones';
import { Envelope, descritores, type TamanhoCampo } from './campo';

export type OpcaoDeEscolha = {
  valor: string;
  rotulo: string;
  /** O porquê de estar desabilitada, ao lado do nome: "· sem acesso ao atendimento". */
  detalhe?: string;
  desabilitada?: boolean;
};

export type CampoEscolhaProps = {
  /** Rótulo visível. Sem ele, `rotuloOculto` passa a ser obrigatório. */
  rotulo?: string;
  /** Rótulo só para leitor de tela — a linha da ficha já escreve o nome. */
  rotuloOculto?: string;
  ajuda?: string;
  erro?: string;
  tamanho?: TamanhoCampo;
  valor: string;
  opcoes: readonly OpcaoDeEscolha[];
  aoEscolher: (valor: string) => void;
  desabilitado?: boolean;
  id?: string;
  /** Abre com um campo de busca no topo da lista: para listas longas. */
  buscavel?: boolean;
  /** O texto apagado do campo enquanto nada está escolhido: "Escolha". */
  vazio?: string;
};

const MARGEM = 4;
const BEIRADA = 8;

export function CampoEscolha({
  rotulo,
  rotuloOculto,
  ajuda,
  erro,
  tamanho = 'padrao',
  valor,
  opcoes,
  aoEscolher,
  desabilitado = false,
  id: idRecebido,
  buscavel = false,
  vazio,
}: CampoEscolhaProps): ReactNode {
  const gerado = useId();
  const id = idRecebido ?? gerado;
  const idDaLista = `${id}-lista`;
  const idDaOpcao = (indice: number) => `${id}-opcao-${indice}`;

  const raiz = useRef<HTMLSpanElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  // A caixa que sobe de camada: a própria lista, ou a busca com a lista embaixo.
  const lista = useRef<HTMLElement | null>(null);
  const busca = useRef<HTMLInputElement>(null);
  const [aberto, definirAberto] = useState(false);
  const [ativa, definirAtiva] = useState(-1);
  const [texto, definirTexto] = useState('');
  const [posicao, definirPosicao] = useState<{
    top: number;
    left: number;
    largura: number;
  } | null>(null);

  const escolhida = opcoes.findIndex((opcao) => opcao.valor === valor);
  const atual = opcoes[escolhida];
  // As que a busca deixa ver, pelo índice em `opcoes`; sem busca, todas.
  const alvo = semAcento(texto.trim());
  const visiveis = opcoes
    .map((_, indice) => indice)
    .filter((indice) => alvo === '' || semAcento(opcoes[indice]?.rotulo ?? '').includes(alvo));

  function abrir(): void {
    if (desabilitado) return;
    definirTexto('');
    definirAtiva(escolhida >= 0 ? escolhida : proxima(opcoes, -1, 1));
    definirAberto(true);
  }

  function fechar(devolverFoco = false): void {
    definirAberto(false);
    if (devolverFoco && buscavel) botao.current?.focus();
  }

  function escolher(indice: number): void {
    const opcao = opcoes[indice];
    if (!opcao || opcao.desabilitada) return;
    fechar(true);
    if (opcao.valor !== valor) aoEscolher(opcao.valor);
  }

  /** A próxima visível e habilitada, no sentido `passo`: as setas dentro da busca. */
  function andar(de: number, passo: 1 | -1): number {
    const aqui = visiveis.indexOf(de);
    const comeco = aqui >= 0 ? aqui : passo === 1 ? -1 : visiveis.length;
    for (let i = comeco + passo; i >= 0 && i < visiveis.length; i += passo) {
      const indice = visiveis[i];
      if (indice !== undefined && !opcoes[indice]?.desabilitada) return indice;
    }
    return aqui >= 0 ? de : -1;
  }

  function buscar(novo: string): void {
    definirTexto(novo);
    const alvoNovo = semAcento(novo.trim());
    const primeira = opcoes.findIndex(
      (opcao) =>
        !opcao.desabilitada && (alvoNovo === '' || semAcento(opcao.rotulo).includes(alvoNovo)),
    );
    definirAtiva(primeira);
  }

  // Posição antes de pintar: entre pintar no canto e mover, a lista piscaria torta.
  useLayoutEffect(() => {
    const caixa = lista.current;
    const ancora = botao.current?.getBoundingClientRect();
    if (!aberto || !caixa || !ancora) return;

    if (typeof caixa.showPopover === 'function' && !caixa.matches(':popover-open')) {
      try {
        caixa.showPopover();
      } catch {
        // Já aberta por outro caminho: a medição abaixo continua valendo.
      }
    }

    const { height: altura, width: natural } = caixa.getBoundingClientRect();
    const cabeEmbaixo = ancora.bottom + MARGEM + altura <= window.innerHeight - BEIRADA;
    // A largura de verdade: a do campo, ou a do texto mais largo, até a tela.
    const largura = Math.min(Math.max(ancora.width, natural), window.innerWidth - 2 * BEIRADA);
    // Começa no canto esquerdo do campo; se passar da tela, termina no canto direito dele.
    const aEsquerda = ancora.left + largura <= window.innerWidth - BEIRADA;
    const left = aEsquerda ? ancora.left : ancora.right - largura;
    definirPosicao({
      top: cabeEmbaixo ? ancora.bottom + MARGEM : Math.max(BEIRADA, ancora.top - MARGEM - altura),
      left: Math.max(BEIRADA, Math.min(left, window.innerWidth - BEIRADA - largura)),
      largura,
    });
    // A busca encolhe a lista: virada para cima, ela precisa descer até o campo.
  }, [aberto, texto]);

  // Com a lista já no lugar (escondida, o foco não pega), a busca recebe o foco.
  const posicionada = posicao !== null;
  useEffect(() => {
    if (aberto && posicionada) busca.current?.focus({ preventScroll: true });
  }, [aberto, posicionada]);

  useEffect(() => {
    if (!aberto) definirPosicao(null);
  }, [aberto]);

  // A opção ativa sempre à vista, numa lista que rola (a equipe pode ser longa).
  useEffect(() => {
    if (!aberto || ativa < 0) return;
    // `idDaOpcao` é derivado do `id`, que não muda.
    document.getElementById(idDaOpcao(ativa))?.scrollIntoView?.({ block: 'nearest' });
  }, [aberto, ativa]);

  /*
   * Fecha no clique fora, e quando a página rola ou muda de tamanho: a lista
   * pertence ao campo, e um campo que sai do lugar a deixaria flutuando sobre
   * outra linha. A rolagem DA PRÓPRIA lista não conta.
   */
  useEffect(() => {
    if (!aberto) return;
    // A lista mora no `body` (portal): o clique nela não é "fora".
    const foraDele = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (!raiz.current?.contains(alvo) && !lista.current?.contains(alvo)) fechar();
    };
    const rolou = (evento: Event) => {
      if (!lista.current?.contains(evento.target as Node)) fechar();
    };
    const mudouTamanho = () => fechar();
    document.addEventListener('mousedown', foraDele);
    window.addEventListener('scroll', rolou, true);
    window.addEventListener('resize', mudouTamanho);
    return () => {
      document.removeEventListener('mousedown', foraDele);
      window.removeEventListener('scroll', rolou, true);
      window.removeEventListener('resize', mudouTamanho);
    };
  }, [aberto]);

  function noTecladoDaBusca(evento: KeyboardEvent<HTMLInputElement>): void {
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        definirAtiva((agora) => andar(agora, 1));
        return;
      case 'ArrowUp':
        evento.preventDefault();
        definirAtiva((agora) => andar(agora, -1));
        return;
      case 'Enter':
        evento.preventDefault();
        escolher(ativa);
        return;
      case 'Escape':
        evento.preventDefault();
        fechar(true);
        return;
      case 'Tab':
        evento.preventDefault();
        fechar(true);
        return;
    }
  }

  function noTeclado(evento: KeyboardEvent<HTMLButtonElement>): void {
    const { key, altKey } = evento;

    if (!aberto) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key) || (altKey && key === 'ArrowDown')) {
        evento.preventDefault();
        abrir();
        return;
      }
      if (key.length === 1) {
        const achada = porLetra(opcoes, key, escolhida);
        if (achada >= 0) escolher(achada);
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        evento.preventDefault();
        definirAtiva((agora) => proxima(opcoes, agora, 1));
        return;
      case 'ArrowUp':
        evento.preventDefault();
        if (altKey) escolher(ativa);
        else definirAtiva((agora) => proxima(opcoes, agora, -1));
        return;
      case 'Home':
        evento.preventDefault();
        definirAtiva(proxima(opcoes, -1, 1));
        return;
      case 'End':
        evento.preventDefault();
        definirAtiva(proxima(opcoes, opcoes.length, -1));
        return;
      case 'Enter':
      case ' ':
        evento.preventDefault();
        escolher(ativa);
        return;
      case 'Escape':
        evento.preventDefault();
        fechar();
        return;
      case 'Tab':
        fechar();
        return;
      default:
        if (key.length === 1) {
          const achada = porLetra(opcoes, key, ativa);
          if (achada >= 0) definirAtiva(achada);
        }
    }
  }

  // Invisível até a conta terminar — ver o `useLayoutEffect`.
  const estiloDaLista = posicao
    ? { top: `${posicao.top}px`, left: `${posicao.left}px`, width: `${posicao.largura}px` }
    : { top: 0, left: 0, visibility: 'hidden' as const };

  const linha = (indice: number): ReactNode => {
    const opcao = opcoes[indice];
    if (!opcao) return null;
    return (
      <li
        key={opcao.valor}
        id={idDaOpcao(indice)}
        role="option"
        className="campo__opcao"
        aria-selected={indice === escolhida}
        aria-disabled={opcao.desabilitada ? true : undefined}
        data-ativa={indice === ativa ? 'sim' : undefined}
        // O foco fica no botão (ou na busca): o clique não pode roubá-lo.
        onMouseDown={(evento) => evento.preventDefault()}
        onMouseEnter={() => !opcao.desabilitada && definirAtiva(indice)}
        onClick={() => escolher(indice)}
      >
        <span className="campo__opcao-marca" aria-hidden="true">
          {indice === escolhida ? <Icone nome="marcado" /> : null}
        </span>
        <span className="campo__opcao-texto">
          <span className="campo__opcao-rotulo">{opcao.rotulo}</span>
          {opcao.detalhe ? (
            // Na linha de baixo, inteiro: cortado, o motivo não se lia.
            <span className="campo__opcao-detalhe">
              <span className="visualmente-oculto">, </span>
              {opcao.detalhe}
            </span>
          ) : null}
        </span>
      </li>
    );
  };

  const classes = [
    'campo__controle',
    'campo__controle--selecao',
    'campo__controle--escolha',
    tamanho === 'alto' ? 'campo__controle--alto' : null,
    tamanho === 'miudo' ? 'campo__controle--miudo' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Envelope id={id} rotulo={rotulo} ajuda={ajuda} erro={erro}>
      <span className="campo__selecao" ref={raiz} data-aberto={aberto ? 'sim' : undefined}>
        <button
          ref={botao}
          id={id}
          type="button"
          role="combobox"
          className={classes}
          aria-haspopup="listbox"
          aria-expanded={aberto}
          aria-controls={idDaLista}
          aria-activedescendant={aberto && ativa >= 0 ? idDaOpcao(ativa) : undefined}
          aria-label={rotulo ? undefined : rotuloOculto}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descritores(id, ajuda, erro)}
          disabled={desabilitado}
          onClick={() => (aberto ? fechar() : abrir())}
          onKeyDown={noTeclado}
        >
          {atual ? (
            <span className="campo__escolha-valor">{atual.rotulo}</span>
          ) : (
            <span className="campo__escolha-valor campo__escolha-valor--vazio">{vazio ?? ''}</span>
          )}
        </button>
        <span className="campo__selecao-seta" aria-hidden="true">
          <Icone nome="abaixo" />
        </span>
        {aberto && buscavel
          ? createPortal(
              <div
                ref={(elemento) => {
                  lista.current = elemento;
                }}
                className="campo__lista campo__lista--busca"
                popover="manual"
                style={estiloDaLista}
              >
                <input
                  ref={busca}
                  type="search"
                  role="combobox"
                  className="campo__busca"
                  aria-label={`Procurar ${(rotulo ?? rotuloOculto ?? '').toLocaleLowerCase('pt-BR')}`}
                  aria-expanded
                  aria-controls={idDaLista}
                  aria-autocomplete="list"
                  aria-activedescendant={ativa >= 0 ? idDaOpcao(ativa) : undefined}
                  placeholder="Procurar"
                  autoComplete="off"
                  value={texto}
                  onChange={(evento) => buscar(evento.currentTarget.value)}
                  onKeyDown={noTecladoDaBusca}
                />
                <ul
                  id={idDaLista}
                  role="listbox"
                  aria-label={rotulo ?? rotuloOculto}
                  className="campo__opcoes"
                >
                  {visiveis.map(linha)}
                </ul>
                {visiveis.length === 0 ? (
                  <p className="campo__nada" role="status">
                    Nada com esse nome.
                  </p>
                ) : null}
              </div>,
              document.body,
            )
          : aberto
            ? createPortal(
                <ul
                  ref={(elemento) => {
                    lista.current = elemento;
                  }}
                  id={idDaLista}
                  role="listbox"
                  aria-labelledby={rotulo ? undefined : id}
                  aria-label={rotulo ?? undefined}
                  className="campo__lista"
                  popover="manual"
                  style={estiloDaLista}
                >
                  {opcoes.map((_, indice) => linha(indice))}
                </ul>,
                document.body,
              )
            : null}
      </span>
    </Envelope>
  );
}

/** A próxima opção habilitada a partir de `de`, no sentido `passo`; fica onde está se não há. */
function proxima(opcoes: readonly OpcaoDeEscolha[], de: number, passo: 1 | -1): number {
  for (let i = de + passo; i >= 0 && i < opcoes.length; i += passo) {
    if (!opcoes[i]?.desabilitada) return i;
  }
  return de >= 0 && de < opcoes.length ? de : -1;
}

/** A próxima opção habilitada que começa pela letra, depois da atual — como no `<select>`. */
function porLetra(opcoes: readonly OpcaoDeEscolha[], letra: string, depoisDe: number): number {
  const alvo = letra.toLocaleLowerCase('pt-BR');
  for (let passo = 1; passo <= opcoes.length; passo++) {
    const indice = (depoisDe + passo + opcoes.length) % opcoes.length;
    const opcao = opcoes[indice];
    if (
      opcao &&
      !opcao.desabilitada &&
      opcao.rotulo
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLocaleLowerCase('pt-BR')
        .startsWith(alvo)
    ) {
      return indice;
    }
  }
  return -1;
}

/** Para comparar nomes: sem acento e sem caixa. */
function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('pt-BR');
}
